import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AreYouSureComponent } from '@vectronic/ui';
import { DialogService } from '@vectronic/util';
import {
  EXTERNAL_MAP_LAYERS_SERVICE,
  ExternalLayerLegendEntry,
} from '../../../services/external-layers/external-map-layers.service';
import { FeatureLayerDetailsService } from '../../../services/map/feature-layer-details.service';

interface LegendSwatch extends ExternalLayerLegendEntry {
  src?: SafeUrl;
}

@Component({
  selector: 'bm-feature-layer-details-panel',
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatSliderModule,
    MatTooltipModule,
  ],
  templateUrl: './feature-layer-details-panel.component.html',
  styleUrl: './feature-layer-details-panel.component.scss',
  host: {
    '(document:pointerup)': 'onPointerUp()',
  },
})
export class FeatureLayerDetailsPanelComponent {
  private externalLayersService = inject(EXTERNAL_MAP_LAYERS_SERVICE, { optional: true });
  private detailsService = inject(FeatureLayerDetailsService);
  private dialogService = inject(DialogService);
  private sanitizer = inject(DomSanitizer);

  layerKey = input.required<string>();

  layers = computed(() => this.externalLayersService?.layers() ?? []);

  layer = computed(() => this.layers().find(({ key }) => key === this.layerKey()));

  legendEntries = signal<LegendSwatch[]>([]);
  legendState = signal<'loading' | 'ready' | 'error' | 'unavailable'>('loading');
  /** Percent transparency (0–100), seeded from drawingInfo, adjustable via the slider. */
  transparency = signal(0);
  /** True while the transparency slider is being dragged (pointer or keyboard). */
  sliderActive = signal(false);
  /** Bubble x-position, clamped so it never leaves the slider. */
  bubbleLeft = computed(() => Math.min(92, Math.max(8, this.transparency())));
  /** Scale labels shown under the slider. */
  readonly sliderMarks = [0, 25, 50, 75, 100];

  constructor() {
    // The legend follows the selected layer: the header dropdown can switch
    // the details to another feature layer at any time.
    effect(() => {
      const key = this.layerKey();
      void this.loadLegend(key);
    });
  }

  onLayerSelected(event: MatSelectChange) {
    const key = event.value;
    // Picking a layer from the dropdown turns it on, like a name click in
    // the layers list.
    const layer = this.layers().find(({ key: layerKey }) => layerKey === key);
    if (layer && !layer.checked) this.externalLayersService?.toggle?.(key, true);
    this.detailsService.open(key);
  }

  onTransparencyChange(value: number) {
    this.transparency.set(value);
    this.externalLayersService?.setLayerTransparency?.(this.layerKey(), value);
  }

  onPointerUp() {
    this.sliderActive.set(false);
  }

  focus() {
    this.externalLayersService?.focusLayer?.(this.layerKey());
  }

  remove() {
    const layer = this.layer();
    if (!layer) return;
    this.dialogService.open(AreYouSureComponent, {
      data: { text: `Remove "${layer.label}" from the layer list?` },
      afterConfirm: () => {
        this.externalLayersService?.removeLayer?.(layer.key);
        this.detailsService.close();
      },
    });
  }

  private async loadLegend(key: string) {
    this.legendState.set('loading');
    this.legendEntries.set([]);
    const service = this.externalLayersService;
    if (!service?.legend) {
      this.legendState.set('unavailable');
      return;
    }
    try {
      const legend = await service.legend(key);
      // A stale response (the user switched layers meanwhile) is dropped.
      if (key !== this.layerKey()) return;
      this.legendEntries.set(legend.entries.map((entry) => this.withSwatch(entry)));
      this.legendState.set(this.legendEntries().length ? 'ready' : 'unavailable');
      // Prefer the live (user-adjusted) value; the drawingInfo value is the seed.
      this.transparency.set(this.layer()?.transparency ?? legend.transparency ?? 0);
    } catch {
      this.legendState.set('error');
    }
  }

  // Angular's sanitizer blocks data: URLs; the swatches are server-rendered
  // base64 PNGs coming from the ArcGIS legend endpoint.
  private withSwatch(entry: ExternalLayerLegendEntry): LegendSwatch {
    if (!entry.imageData) return { ...entry };
    return {
      ...entry,
      // eslint-disable-next-line sonarjs/no-angular-bypass-sanitization
      src: this.sanitizer.bypassSecurityTrustUrl(
        `data:${entry.contentType ?? 'image/png'};base64,${entry.imageData}`,
      ),
    };
  }
}
